import { Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import { CorePermissions } from 'app/appPermissions';
import { MprcContainer, MprcDialogTitleClose, MprcIconButtonDropdown } from 'components';
import { ButtonCancel, ButtonConfirm } from 'components/buttons';
import { AutoCompleteDataModel } from 'components/MprcAutoComplete';
import { ChannelAutoCompleteDataModel, ManagerAutoCompleteDataModel, TrainingAutoCompleteDataModel } from 'components/MprcAutoComplete/AutoCompleteDataModel';
import { AutoCompleteHierarchicalModel, TopicAutoCompleteHierarchicalModel, UnitAutoCompleteHierarchicalModel } from 'components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel';
import { MprcDropdownBuildGroupedItems, MprcDropdownItem, MprcDropdownItems } from 'components/MprcIconButtonDropdown';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { GroupInfo, GroupType, GroupUsages, UnitTypeResponse } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupAutoComplete } from 'views/foundation/group/components/GroupAutoComplete';
import { TopicAutoCompleteHierarchical } from 'views/foundation/topic/components/TopicAutoCompleteHierarchical';
import { UnitAutoCompleteHierarchical } from 'views/foundation/unit/components/UnitAutoCompleteHierarchical';
import { ManagerAutoComplete } from 'views/foundation/user/components/ManagerAutoComplete';
import { UserAutoComplete } from 'views/foundation/user/components/UserAutoComplete';
import { ChannelAutoComplete } from 'views/knowledge/channels/components/ChannelAutoComplete';
import { TrainingAutoComplete } from 'views/lms/training/components/TrainingAutoComplete';

////////////////////////////////////////////////////////////////////////////
export interface AudienceOption {
    groupType: GroupType;
    groupTypeId?: string;
    groupTypeName?: string;
    group?: GroupInfo;
}

interface AudienceItem {
    group?: GroupInfo;
    autoComplete?: AutoCompleteDataModel | AutoCompleteHierarchicalModel;
}

type AudienceType = GroupType.AllUsers |
    GroupType.InternalUsers |
    GroupType.ExternalUsers |
    GroupType.OwnUser |
    GroupType.Static |
    GroupType.Dynamic |
    GroupType.ChannelUsers |
    GroupType.DirectSubordinates |
    GroupType.AllSubordinates |
    GroupType.TopicOptionalUsers |
    GroupType.TopicRequiredUsers |
    GroupType.TrainingApprovedUsers |
    GroupType.UnitUsers;

export interface CustomAudienceType {
    type: AudienceType;
    label?: string;
}

interface AudienceSelectorDropdownProps {
    icon: React.ReactNode;
    text?: string;
    disabled?: boolean;
    // Lista reduzida - mostra apenas estes tipos
    includeOnlyTypes?: CustomAudienceType[];
    // Exclui poucos tipos - mostra todos exceto estes
    excludeTypes?: AudienceType[];
    //Restrição de seleção de grupos
    groupUsages?: GroupUsages;
    onChange: (group?: GroupInfo | null) => void;
    renderCustomDialog?: (props: AudienceSelectorDialogBaseProps) => React.ReactNode;
}

////////////////////////////////////////////////////////////////////////////

export const AudienceSelectorDropdown: React.FC<AudienceSelectorDropdownProps> = (props) => {

    const { includeOnlyTypes = [], excludeTypes = [], icon, text, disabled, groupUsages, onChange, renderCustomDialog } = props;

    const api = useCoreApi();
    const { permissionExplorer } = useSession();
    const { t } = useTranslation("core");

    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [audienceOption, setAudienceOption] = useState<AudienceOption>();
    const [unitTypes, setUnitTypes] = useState<UnitTypeResponse[]>([]);
    const [internalGroups, setInternalGroups] = useState<GroupInfo[]>([]);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const getInternalGroup = (groupType: GroupType) => internalGroups.find(p => p.groupType == groupType);

    ///
    const addMenuItem = (menuGroup: MprcDropdownItems<AudienceOption>, audienceType: AudienceType, defaultLabel: string, value?: Partial<AudienceOption>) => {
        const customConfig = includeOnlyTypes.find((t: CustomAudienceType) => t.type === audienceType);

        if ((includeOnlyTypes.length == 0 || customConfig) && !excludeTypes.includes(audienceType)) {
            const label = customConfig?.label ?? defaultLabel;
            menuGroup.push({ text: label, value: { ...value, groupType: audienceType } });
        }
    };

    ///
    const getMenuItems = (): MprcDropdownItem<AudienceOption>[] => {

        const group1: MprcDropdownItems<AudienceOption> = [];

        if (permissionExplorer.hasOne([CorePermissions.GroupsViewGeneral])) {
            addMenuItem(group1, GroupType.AllUsers, t("Group.AudienceSelector.AllUsersOption"), { group: getInternalGroup(GroupType.AllUsers) });
            addMenuItem(group1, GroupType.InternalUsers, t("Group.AudienceSelector.InternalUsersOption"), { group: getInternalGroup(GroupType.InternalUsers) });
            addMenuItem(group1, GroupType.ExternalUsers, t("Group.AudienceSelector.ExternalUsersOption"), { group: getInternalGroup(GroupType.ExternalUsers) });
        }

        addMenuItem(group1, GroupType.OwnUser, t("Group.AudienceSelector.UsersOption"));

        const group2: MprcDropdownItems<AudienceOption> = [];
        addMenuItem(group2, GroupType.Static, t("Group.AudienceSelector.GroupMembersOption"));

        if (permissionExplorer.hasOne([CorePermissions.GroupsViewGeneral])) {
            addMenuItem(group2, GroupType.ChannelUsers, t("Group.AudienceSelector.ChannelMembersOption"));
            addMenuItem(group2, GroupType.TopicOptionalUsers, t("Group.AudienceSelector.TopicOptionalUsersOption"));
            addMenuItem(group2, GroupType.TopicRequiredUsers, t("Group.AudienceSelector.TopicRequiredUsersOption"));
            addMenuItem(group2, GroupType.TrainingApprovedUsers, t("Group.AudienceSelector.TrainingApprovedUsersOption"));
        }

        const group3: MprcDropdownItems<AudienceOption> = [];
        unitTypes.forEach(item =>
            addMenuItem(
                group3,
                GroupType.UnitUsers,
                item.name + "...",
                { groupType: GroupType.UnitUsers, groupTypeId: item.unitTypeId, groupTypeName: item.name }
            )
        );

        const group4: MprcDropdownItems<AudienceOption> = [];
        addMenuItem(group4, GroupType.DirectSubordinates, t("Group.AudienceSelector.DirectSubordinatesOption"));
        addMenuItem(group4, GroupType.AllSubordinates, t("Group.AudienceSelector.AllSubordinatesOption"));

        return MprcDropdownBuildGroupedItems<AudienceOption>([group1, group2, group3, group4]);
    };

    ///
    const handleMenuOption = (selectedOption: AudienceOption) => {
        setAudienceOption(selectedOption);

        if (!renderCustomDialog && selectedOption.group) {
            onChange(selectedOption.group);
        }
        else {
            setOpenDialog(true);
        }
    };

    useEffect(() => {
        api.v1.unitType.getUnitTypes().then(response => setUnitTypes(response.data));
        api.v1.group.getInternalGroups().then(response => setInternalGroups(response.data));
    }, []);

    return (
        <>
            {openDialog &&
                (renderCustomDialog
                    ? renderCustomDialog({
                        audienceOption: audienceOption!,
                        onClose: () => { setOpenDialog(false); },
                    })
                    : <AudienceSelectorDialog
                        audienceOption={audienceOption!}
                        audienceGroupUsage={groupUsages}
                        onClose={(group) => { onChange(group); setOpenDialog(false); }}
                        {...props}
                    />
                )
            }

            <MprcIconButtonDropdown
                disabled={disabled}
                separator="blank"
                text={text}
                icon={icon}
                items={getMenuItems()}
                onChange={option => handleMenuOption(option)}
            />
        </>
    );
};

interface AudienceSelectorDialogProps extends AudienceSelectorDialogBaseProps {
    children?: React.ReactNode;
}

export interface AudienceSelectorDialogBaseProps {
    subtitle?: string;
    audienceOption: AudienceOption;
    audienceGroupUsage?: GroupUsages;
    onClose: (group?: GroupInfo | null) => void;
}

////////////////////////////////////////////////////////////////////////////

export const AudienceSelectorDialog: React.FC<AudienceSelectorDialogProps> = (props) => {

    const { subtitle, audienceOption, children, audienceGroupUsage = GroupUsages.Training, onClose } = props;

    const { t } = useTranslation("core");

    const [selectedAudience, setSelectedAudience] = useState<AudienceItem | undefined>();

    const canSave = audienceOption.group != undefined || selectedAudience?.autoComplete != undefined;

    ///
    const handleSelect = (group: GroupInfo | undefined, item: AutoCompleteDataModel | AutoCompleteHierarchicalModel | undefined) => {
        setSelectedAudience({ group, autoComplete: item });
    };

    ///
    const handleConfirm = () => {
        onClose(audienceOption.group ?? selectedAudience!.group);
        setSelectedAudience(undefined);
    };

    return (
        <Dialog open maxWidth="sm" fullWidth>
            <MprcDialogTitleClose
                title={t("Group.AudienceSelector.Title")}
                subtitle={subtitle}
                onClose={() => onClose()}
                shrinkPadding
            />
            <DialogContent>
                <MprcContainer>
                    {(audienceOption.groupType === GroupType.Static || audienceOption.groupType === GroupType.Dynamic) &&
                        <GroupAutoComplete
                            groupUsages={audienceGroupUsage}
                            placeholder={t("Group.AudienceSelector.SelectGroupPlaceholder")}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                            onChange={(items: AutoCompleteDataModel[]) => {
                                if (items.length > 0) {
                                    handleSelect({ id: items[0].key, name: items[0].label, groupType: GroupType.Static }, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                        />
                    }
                    {(audienceOption.groupType === GroupType.UnitAllUsers || audienceOption.groupType === GroupType.UnitUsers) &&
                        <UnitAutoCompleteHierarchical
                            label={t("Group.AudienceSelector.SelectUnitPlaceholder", { name: audienceOption.groupTypeName })}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as UnitAutoCompleteHierarchicalModel[] : []}
                            multiSelect={false}
                            selectedMode="free"
                            unitTypeId={audienceOption.groupTypeId}
                            showDescendantOption={true}
                            onChange={(items: (AutoCompleteHierarchicalModel | UnitAutoCompleteHierarchicalModel)[]) => {
                                if (items.length > 0) {
                                    const unit = items[0] as UnitAutoCompleteHierarchicalModel;
                                    handleSelect(unit.includeDescendants ? unit.allUsersGroup : unit.usersGroup, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                            onError={e => MprcRequestSnackbarErrorReport(e)}
                        />
                    }
                    {audienceOption.groupType === GroupType.OwnUser &&
                        <UserAutoComplete
                            placeholder={t("Group.AudienceSelector.SelectUserPlaceholder")}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                            onChange={(items: AutoCompleteDataModel[]) => {
                                if (items.length > 0) {
                                    handleSelect({ id: items[0].key, name: items[0].label, groupType: GroupType.OwnUser }, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                        />
                    }
                    {audienceOption.groupType === GroupType.ChannelUsers &&
                        <ChannelAutoComplete
                            placeholder={t("Group.AudienceSelector.SelectChannelPlaceholder")}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as ChannelAutoCompleteDataModel[] : []}
                            onChange={(items: ChannelAutoCompleteDataModel[]) => {
                                if (items.length > 0) {
                                    handleSelect(items[0].usersGroup, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                        />
                    }
                    {(audienceOption.groupType === GroupType.DirectSubordinates || audienceOption.groupType === GroupType.AllSubordinates) &&
                        <ManagerAutoComplete
                            placeholder={t("Group.AudienceSelector.SelectManagerPlaceholder")}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                            onChange={(items: ManagerAutoCompleteDataModel[]) => {
                                if (items.length > 0) {
                                    handleSelect(audienceOption.groupType === GroupType.DirectSubordinates ? items[0].directSubordinatesGroup : items[0].allSubordinatesGroup, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                        />
                    }
                    {(audienceOption.groupType === GroupType.TopicRequiredUsers || audienceOption.groupType === GroupType.TopicOptionalUsers) &&
                        <TopicAutoCompleteHierarchical
                            selectedMode="free"
                            placeholder={t("Group.AudienceSelector.SelectTopicPlaceholder")}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteHierarchicalModel[] : []}
                            onChange={(items) => {
                                if (items.length > 0) {
                                    var item = items[0] as TopicAutoCompleteHierarchicalModel;
                                    handleSelect(audienceOption.groupType === GroupType.TopicOptionalUsers ? item.optionalGroup : item.requiredGroup, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                            onError={e => MprcRequestSnackbarErrorReport(e)}
                        />
                    }
                    {audienceOption.groupType === GroupType.TrainingApprovedUsers &&
                        <TrainingAutoComplete
                            placeholder={t("Group.AudienceSelector.SelectTrainingPlaceholder")}
                            selectedItems={selectedAudience?.autoComplete ? [selectedAudience.autoComplete] as AutoCompleteDataModel[] : []}
                            onChange={(items: TrainingAutoCompleteDataModel[]) => {
                                if (items.length > 0) {
                                    handleSelect(items[0].approvedUsersGroup, items[0]);
                                }
                                else {
                                    handleSelect(undefined, undefined);
                                }
                            }}
                        />
                    }
                </MprcContainer>
                {children}
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2} alignItems="flex-end">
                    <ButtonCancel onClick={() => onClose()} />
                    <ButtonConfirm disabled={!canSave} variant="contained" onClick={handleConfirm} />
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
